import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { CalloutCarousel } from '../../CalloutCarousel';
import DashboardWelcomeHeader from '../DashboardWelcomeHeader';

interface IDashboardWelcomeCardProps {
  /** Pre-resolved informal name for the greeting. Caller picks `preferredName || firstName`. Falls back to a generic greeting when absent. */
  greetingName?: string | null;
  className?: string;
}

// Match the card header's horizontal padding so callout cards left-align with
// the "Welcome back" greeting: `px-5` (20px) below md, `md:px-6` (24px) above.
const CAROUSEL_EDGE_PADDING_MOBILE_PX = 20;
const CAROUSEL_EDGE_PADDING_DESKTOP_PX = 24;

/**
 * Mist-themed welcome card at the top of the dashboard. Composes
 * {@link DashboardWelcomeHeader} (greeting + conditional sub-line) and
 * {@link CalloutCarousel} inside a rounded container with a halftone texture
 * overlay. Full-bleed below `md`, bounded card with border at `md+`.
 *
 * Manages `hasCallouts` internally via the carousel's `onCalloutCountChange`
 * callback so the hosting pane doesn't need to query callout data.
 */
const DashboardWelcomeCard = ({ greetingName, className }: IDashboardWelcomeCardProps) => {
  const isMdUp = useBreakpoint('md');
  const carouselEdgePadding = isMdUp ? CAROUSEL_EDGE_PADDING_DESKTOP_PX : CAROUSEL_EDGE_PADDING_MOBILE_PX;

  const [hasCallouts, setHasCallouts] = React.useState(false);
  const handleCalloutCountChange = React.useCallback((count: number) => setHasCallouts(count > 0), []);

  return (
    <div
      className={cx(
        // Full-bleed below md (escapes Entity.Body's px-4); bounded card with rounded
        // corners + 2px border at md+ (tablet and desktop), sitting inside Entity.Body's
        // px-4 / lg:px-8 content rail.
        // `min-w-0 w-full` keeps the card from being stretched horizontally by the
        // carousel's overflowing children — the carousel manages its own internal
        // scroll, so the card itself stays bounded by its flex parent.
        'bg-utility-mist-100 border-utility-mist-200 relative isolate -mx-4 w-auto min-w-0 overflow-clip md:mx-0 md:w-full md:rounded-2xl md:border-2',
        className
      )}
    >
      {/*
        Decorative halftone texture overlay — rendered as a background-image so we can
        pin the SVG to its native pixel size and anchor it top-right. The source SVGs
        from Figma use `preserveAspectRatio="none"` and would distort if scaled by an
        `<img>` element to a non-native aspect ratio. With background-size fixed, the
        dense dot cluster always sits flush with the top-right edge regardless of how
        wide or tall the card grows.

        Both assets anchored top-right so the dense dot cluster always sits
        flush with the card's right edge regardless of viewport width.
        Below md: full-bleed mobile asset (402×270).
        md+: bounded-card asset (1112×363).
      */}
      <div
        aria-hidden="true"
        className="bg-size-[402px_270px] bg-top-right pointer-events-none absolute inset-0 -z-10 bg-[url('/images/dashboard-welcome-card/welcome-card-texture-mobile.svg')] bg-no-repeat md:bg-[url('/images/dashboard-welcome-card/welcome-card-texture-desktop.svg')] md:bg-[length:1112px_363px]"
      />
      <div className="flex flex-col gap-6 pb-6 pt-6 md:pb-5 md:pt-10">
        <DashboardWelcomeHeader greetingName={greetingName} hasCallouts={hasCallouts} />
        <CalloutCarousel
          bleed={false}
          edgePadding={carouselEdgePadding}
          onCalloutCountChange={handleCalloutCountChange}
        />
      </div>
    </div>
  );
};

export default DashboardWelcomeCard;
